// v49 RRHH — /app/rrhh/expedientes/:id
// Expediente digital del colaborador, 7 pestanas (spec de ajustes §6).
//
// Regla de oro (spec original §5): los campos que generan accion de personal se
// muestran con candado y boton "Registrar accion". No es cosmetico: el PUT del
// backend responde 409 si llegan en el body.
//
// La completitud NO se calcula aca (D-2): la manda el backend en el detalle,
// igual que en el listado, para que la regla viva en un solo lugar.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config/api';
import { useAdmin } from '../context/AdminContext';
import { formatFecha } from '../utils/fecha';
import {
  cargarListas, ESTADOS_LABORALES, CONTRATOS_CON_VENCIMIENTO,
  MODALIDADES_REMOTAS, ETIQUETA_CAMPO,
  paraInputFecha, fmtMonto, fmtBytes, msgError, textoValorAccion,
  btnPrimario, btnSecundario, btnNavy, tarjeta, thStyle, tdStyle
} from '../utils/rrhh';
import {
  Dato, DatoBloqueado, Campo, CampoCheck, CampoMultiple, Badge, Aviso,
  tonoEstadoLaboral, tonoEstadoAccion
} from '../components/RrhhCampos';
import ModalAccionPersonal from '../components/ModalAccionPersonal';

const PESTANAS = [
  'Ficha personal', 'Formación y experiencia', 'Documentos del expediente',
  'Ficha laboral', 'Salarial', 'Activos', 'Historial'
];

// Campos DATE del expediente: si van vacios se mandan como null, nunca como ''.
const CAMPOS_FECHA = [
  'fecha_nacimiento', 'fecha_ingreso', 'contrato_inicia', 'contrato_vence',
  'periodo_prueba_hasta', 'licencia_vence'
];

export default function RrhhExpedienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [exp, setExp]              = useState(null);
  const [listas, setListas]        = useState({});
  const [colaboradores, setColabs] = useState([]);
  const [contactos, setContactos]  = useState([]);
  const [licencias, setLicencias]  = useState([]);
  const [tab, setTab]              = useState(PESTANAS[0]);
  const [cargando, setCargando]    = useState(true);
  const [error, setError]          = useState('');
  const [aviso, setAviso]          = useState('');
  const [modalAccion, setModalAccion] = useState(null);
  const [modalBaja, setModalBaja]     = useState(false);
  const [modalReactivar, setModalReactivar] = useState(false);

  const cargarExpediente = useCallback(async () => {
    try {
      const [rExp, rCont, rLic] = await Promise.all([
        axios.get(`${API}/rrhh-colaboradores?id=${id}`),
        axios.get(`${API}/rrhh-colaboradores?action=contactos&id=${id}`),
        axios.get(`${API}/rrhh-colaboradores?action=licencias&id=${id}`)
      ]);
      setExp(rExp.data);
      setContactos(rCont.data || []);
      setLicencias(rLic.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [id]);

  useEffect(() => { setCargando(true); cargarExpediente(); }, [cargarExpediente]);

  useEffect(() => {
    (async () => {
      try { setListas(await cargarListas()); } catch (e) { /* los selects quedan vacios */ }
      try {
        const r = await axios.get(`${API}/rrhh-colaboradores`);
        setColabs(r.data || []);
      } catch (e) { /* jefatura sin opciones */ }
    })();
  }, []);

  // PUT del expediente. El backend distingue "la clave no vino" de "vino nula",
  // asi que mandar null vacia el campo a proposito (D-4).
  const guardar = async (cambios) => {
    setError(''); setAviso('');
    const body = { ...cambios };
    Object.keys(body).forEach(k => { if (CAMPOS_FECHA.includes(k) && body[k] === '') body[k] = null; });
    try {
      await axios.put(`${API}/rrhh-colaboradores?id=${id}`, body);
      await cargarExpediente();
      setAviso('Cambios guardados.');
      return true;
    } catch (e) { setError(msgError(e)); return false; }
  };

  if (cargando) return <div style={{ padding: 40, fontFamily: 'Segoe UI, sans-serif', color: '#888' }}>Cargando expediente...</div>;
  if (!exp) return (
    <div style={{ padding: 40, fontFamily: 'Segoe UI, sans-serif' }}>
      <Aviso texto={error || 'Expediente no encontrado.'} />
      <button onClick={() => navigate('/app/rrhh/expedientes')} style={btnSecundario}>Volver al listado</button>
    </div>
  );

  const faltantes = exp.campos_faltantes || [];
  const props = { exp, listas, guardar, setError, setAviso };

  return (
    <div style={{ padding: '24px', fontFamily: 'Segoe UI, sans-serif', maxWidth: '1200px' }}>

      {/* Header del expediente */}
      <div style={{ background: '#012E40', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/app/rrhh/expedientes')}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer', padding: 0 }}>
              &larr; Expedientes
            </button>
            <span style={{ color: '#87B749', fontSize: '13px', fontWeight: '700' }}>{exp.codigo}</span>
            <span style={{ color: '#fff', fontSize: '17px', fontWeight: '700' }}>{exp.nombre_completo}</span>
            <Badge texto={exp.estado_laboral} tono={tonoEstadoLaboral(exp.estado_laboral)} />
            {exp.estado_expediente === 'Inactivo' && <Badge texto="Expediente inactivo" tono="rojo" />}
            {exp.expediente_completo
              ? <Badge texto="Completo" tono="verde" />
              : <Badge texto={`Incompleto (${faltantes.length})`} tono="ambar" />}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setModalAccion('Puesto')} style={btnPrimario}>Registrar accion de personal</button>
            {exp.estado_expediente === 'Activo' ? (
              <button onClick={() => setModalBaja(true)}
                style={{ ...btnSecundario, background: 'transparent', color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.3)' }}>
                Dar de baja
              </button>
            ) : (
              <button onClick={() => setModalReactivar(true)}
                style={{ ...btnSecundario, background: 'transparent', color: '#87B749', borderColor: 'rgba(135,183,73,0.5)' }}>
                Reactivar (recontratacion)
              </button>
            )}
          </div>
        </div>
        {faltantes.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
            Falta completar: {faltantes.map(x => ETIQUETA_CAMPO[x] || x).join(' · ')}
          </div>
        )}
      </div>

      <Aviso texto={error} />
      <Aviso texto={aviso} tipo="ok" />

      {/* Pestanas */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
        {PESTANAS.map(p => (
          <button key={p} onClick={() => { setTab(p); setError(''); setAviso(''); }}
            style={{
              padding: '9px 14px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: tab === p ? '700' : '500',
              color: tab === p ? '#012E40' : '#888',
              borderBottom: `2px solid ${tab === p ? '#87B749' : 'transparent'}`, marginBottom: '-1px'
            }}>
            {p}
          </button>
        ))}
      </div>

      {tab === 'Ficha personal' && (
        <TabFichaPersonal {...props} contactos={contactos} licencias={licencias} recargar={cargarExpediente} />
      )}
      {tab === 'Formación y experiencia'  && <TabFormacion colaboradorId={id} listas={listas} setError={setError} recargar={cargarExpediente} />}
      {tab === 'Documentos del expediente' && <TabDocumentos colaboradorId={id} setError={setError} setAviso={setAviso} />}
      {tab === 'Ficha laboral'  && <TabFichaLaboral {...props} onAccion={setModalAccion} />}
      {tab === 'Salarial'       && <TabSalarial {...props} onAccion={setModalAccion} />}
      {tab === 'Activos'        && <TabActivos colaboradorId={id} listas={listas} setError={setError} />}
      {tab === 'Historial'      && <TabHistorial colaboradorId={id} listas={listas} colaboradores={colaboradores} moneda={exp.moneda} />}

      {modalAccion && (
        <ModalAccionPersonal
          colaborador={exp} listas={listas} colaboradores={colaboradores} tipoInicial={modalAccion}
          onClose={() => setModalAccion(null)}
          onGuardado={() => {
            setModalAccion(null);
            setAviso('Accion guardada como Borrador. Revisala y enviala desde la bandeja de acciones.');
            cargarExpediente();
          }}
        />
      )}

      {modalBaja && (
        <ModalBaja colaboradorId={id} listas={listas}
          onClose={() => setModalBaja(false)}
          onHecho={() => { setModalBaja(false); setAviso('Expediente dado de baja y periodo laboral cerrado.'); cargarExpediente(); }} />
      )}

      {modalReactivar && (
        <ModalReactivar colaboradorId={id}
          onClose={() => setModalReactivar(false)}
          onHecho={() => { setModalReactivar(false); setAviso('Expediente reactivado con un periodo laboral nuevo.'); cargarExpediente(); }} />
      )}
    </div>
  );
}

// ── Panel base: modo Ver / Editar ───────────────────────────────────────
function Panel({ titulo, editando, onEditar, onCancelar, onGuardar, guardando, children, sinEdicion }) {
  return (
    <div style={{ ...tarjeta, padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>{titulo}</h3>
        {!sinEdicion && (editando ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onCancelar} style={btnSecundario}>Cancelar</button>
            <button onClick={onGuardar} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        ) : (
          <button onClick={onEditar} style={btnSecundario}>Editar</button>
        ))}
      </div>
      {children}
    </div>
  );
}

const grid = (cols = 3) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px' });

// Bloque con titulo propio dentro de un panel.
//
// Sin esto, los campos que dependen de una casilla quedaban al costado y se
// leian como campos sueltos en una pared continua: JC no encontro donde elegir
// el tipo de licencia aunque el campo estaba ahi (1-ago). El titulo y el salto
// de linea dejan claro que esos campos pertenecen a la casilla de arriba.
function SubSeccion({ titulo, children }) {
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#012E40', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '12px' }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

// ── Ficha personal ──────────────────────────────────────────────────────
function TabFichaPersonal({ exp, listas, guardar, contactos, licencias, recargar, setError }) {
  const esAdmin = useAdmin();
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [tiposLic, setTiposLic] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const abrir = () => {
    setForm({
      ...(esAdmin ? { azure_oid: exp.azure_oid || '' } : {}),
      identificacion: exp.identificacion || '', nombre_completo: exp.nombre_completo || '',
      fecha_nacimiento: paraInputFecha(exp.fecha_nacimiento), nacionalidad: exp.nacionalidad || '',
      estado_civil_id: exp.estado_civil_id, direccion: exp.direccion || '',
      telefono_principal: exp.telefono_principal || '', telefono_secundario: exp.telefono_secundario || '',
      correo_personal: exp.correo_personal || '', correo_empresarial: exp.correo_empresarial || '',
      incorporado_colegio: !!exp.incorporado_colegio, colegio_profesional_id: exp.colegio_profesional_id,
      tiene_licencia_conducir: !!exp.tiene_licencia_conducir, licencia_vence: paraInputFecha(exp.licencia_vence)
    });
    setTiposLic(licencias.map(l => String(l.tipo_licencia_id)));
    setEditando(true);
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setGuardando(true);
    // Si el indicador esta en no, se limpian sus datos dependientes.
    const body = { ...form };
    if (!body.incorporado_colegio) body.colegio_profesional_id = null;
    if (!body.tiene_licencia_conducir) body.licencia_vence = null;
    const ok = await guardar(body);
    if (ok) {
      try {
        await axios.put(`${API}/rrhh-colaboradores?action=licencias&id=${exp.id}`, {
          tipos: body.tiene_licencia_conducir ? tiposLic : []
        });
        await recargar();
      } catch (e) { setError(msgError(e)); }
      setEditando(false);
    }
    setGuardando(false);
  };

  return (
    <>
      <Panel titulo="Ficha personal" editando={editando} guardando={guardando}
        onEditar={abrir} onCancelar={() => setEditando(false)} onGuardar={submit}>
        {editando ? (
          <>
            <div style={grid(3)}>
              <Campo label="Identificacion" requerido valor={form.identificacion} onChange={v => f('identificacion', v)} />
              <Campo label="Nombre completo" requerido ancho={2} valor={form.nombre_completo} onChange={v => f('nombre_completo', v)} />
              <Campo label="Fecha de nacimiento" tipo="date" valor={form.fecha_nacimiento} onChange={v => f('fecha_nacimiento', v)} />
              <Campo label="Nacionalidad" valor={form.nacionalidad} onChange={v => f('nacionalidad', v)} />
              <Campo label="Estado civil" opciones={listas.rrhh_estado_civil || []} valor={form.estado_civil_id} onChange={v => f('estado_civil_id', v)} />
              <Campo label="Telefono principal" valor={form.telefono_principal} onChange={v => f('telefono_principal', v)} />
              <Campo label="Telefono secundario" valor={form.telefono_secundario} onChange={v => f('telefono_secundario', v)} />
              <Campo label="Correo personal" tipo="email" valor={form.correo_personal} onChange={v => f('correo_personal', v)} />
              <Campo label="Correo empresarial" tipo="email" valor={form.correo_empresarial} onChange={v => f('correo_empresarial', v)} />
              <Campo label="Direccion" ancho={2} valor={form.direccion} onChange={v => f('direccion', v)} />
            </div>

            <SubSeccion titulo="Colegio profesional">
              <div style={grid(3)}>
                <CampoCheck label="¿Está incorporado?" valor={form.incorporado_colegio} onChange={v => f('incorporado_colegio', v)} />
              </div>
              {form.incorporado_colegio && (
                <div style={{ ...grid(3), marginTop: 12 }}>
                  <Campo label="¿A cuál colegio?" ancho={2} opciones={listas.rrhh_colegio_profesional || []}
                    valor={form.colegio_profesional_id} onChange={v => f('colegio_profesional_id', v)} />
                </div>
              )}
            </SubSeccion>

            <SubSeccion titulo="Licencia de conducir">
              <div style={grid(3)}>
                <CampoCheck label="¿Tiene licencia?" valor={form.tiene_licencia_conducir} onChange={v => f('tiene_licencia_conducir', v)} />
              </div>
              {form.tiene_licencia_conducir && (
                <>
                  <div style={{ ...grid(2), marginTop: 12 }}>
                    <CampoMultiple label="Tipos de licencia" opciones={listas.rrhh_tipo_licencia || []} valor={tiposLic} onChange={setTiposLic} />
                    <Campo label="Vence (licencia B)" tipo="date" valor={form.licencia_vence} onChange={v => f('licencia_vence', v)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                    Marcá todas las que tenga. Los tipos son informativos: la alerta de vencimiento solo se
                    genera para quien tenga licencia B, que es la que habilita el uso de vehículos en giras.
                  </div>
                </>
              )}
            </SubSeccion>

            {/* Identidad de Azure AD (H-4). Solo administrador: el backend
                responde 403 a cualquier otro, y rechaza un oid ya usado por
                otro expediente. Sin este dato, la omision por autoaprobacion
                no dispara porque el sistema no sabe quien es quien. */}
            {esAdmin && (
              <SubSeccion titulo="Identidad de Azure AD">
                <Campo label="oid de la cuenta" valor={form.azure_oid}
                  onChange={v => f('azure_oid', v)}
                  placeholder="Solo si esta persona aprueba acciones de personal" />
                <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                  Liga el expediente con la cuenta de Azure AD. Se necesita unicamente para que el sistema
                  reconozca que el aprobador es el sujeto de una accion y omita ese paso. Debe ser el oid,
                  no el correo, y no puede repetirse en otro expediente.
                </div>
              </SubSeccion>
            )}
          </>
        ) : (
          <>
            <div style={grid(3)}>
              <Dato label="Codigo" valor={exp.codigo} />
              <Dato label="Identificacion" valor={exp.identificacion} />
              <Dato label="Nombre completo" valor={exp.nombre_completo} />
              <Dato label="Fecha de nacimiento" valor={formatFecha(exp.fecha_nacimiento)} />
              <Dato label="Nacionalidad" valor={exp.nacionalidad} />
              <Dato label="Estado civil" valor={exp.estado_civil} />
              <Dato label="Telefono principal" valor={exp.telefono_principal} />
              <Dato label="Telefono secundario" valor={exp.telefono_secundario} />
              <Dato label="Correo personal" valor={exp.correo_personal} />
              <Dato label="Correo empresarial" valor={exp.correo_empresarial} />
              <Dato label="Direccion" ancho={2} valor={exp.direccion} />
            </div>
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f0f0f0', ...grid(3) }}>
              {/* Derivado de Formacion (A3): no existe como columna en ninguna tabla. */}
              <Dato label="Ultimo grado academico" valor={exp.ultimo_grado_academico} />
              <Dato label="Colegio profesional"
                valor={exp.incorporado_colegio ? (exp.colegio_profesional || 'Sí, sin colegio indicado') : 'No incorporado'} />
              <Dato label="Licencia de conducir"
                valor={exp.tiene_licencia_conducir
                  ? (licencias.length ? licencias.map(l => l.tipo).join(' · ') : 'Sí, sin tipos indicados')
                  : 'No'} />
              {esAdmin && (
                <Dato label="Identidad de Azure AD"
                  valor={exp.azure_oid ? 'Ligada' : 'Sin ligar'} />
              )}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
              El ultimo grado academico se deriva de los titulos registrados en Formacion; no se digita.
              La vigencia de la colegiatura vive en el documento "Certificacion de Colegiatura".
            </div>
          </>
        )}
      </Panel>

      <Contactos colaboradorId={exp.id} contactos={contactos} listas={listas}
        recargar={recargar} setError={setError} />
    </>
  );
}

// Contactos de emergencia (D13: lista, al menos uno para completitud).
function Contactos({ colaboradorId, contactos, listas, recargar, setError }) {
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const nuevo  = () => setForm({ nombre: '', parentesco_id: null, telefono: '', telefono_secundario: '' });
  const editar = (c) => setForm({ id: c.id, nombre: c.nombre, parentesco_id: c.parentesco_id, telefono: c.telefono, telefono_secundario: c.telefono_secundario || '' });

  const submit = async () => {
    if (!form.nombre?.trim() || !form.telefono?.trim()) { setError('Nombre y telefono del contacto son obligatorios.'); return; }
    setGuardando(true); setError('');
    try {
      if (form.id) await axios.put(`${API}/rrhh-colaboradores?action=contactos&id=${form.id}`, form);
      else         await axios.post(`${API}/rrhh-colaboradores?action=contactos&id=${colaboradorId}`, form);
      setForm(null);
      await recargar();
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`Eliminar el contacto ${c.nombre}?`)) return;
    try { await axios.delete(`${API}/rrhh-colaboradores?action=contactos&id=${c.id}`); await recargar(); }
    catch (e) { setError(msgError(e)); }
  };

  return (
    <div style={{ ...tarjeta, padding: '20px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>
          Contactos de emergencia
          {contactos.length === 0 && <span style={{ marginLeft: 8 }}><Badge texto="Se requiere al menos uno" tono="ambar" /></span>}
        </h3>
        <button onClick={nuevo} style={btnSecundario}>+ Agregar</button>
      </div>

      {contactos.length === 0 && !form
        ? <div style={{ color: '#bbb', fontSize: 13 }}>Sin contactos registrados.</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#f8f9fa' }}>
              {['Nombre', 'Parentesco', 'Telefono', 'Telefono 2', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>
              {contactos.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                  <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{c.nombre}</td>
                  <td style={tdStyle}>{c.parentesco || '—'}</td>
                  <td style={tdStyle}>{c.telefono}</td>
                  <td style={tdStyle}>{c.telefono_secundario || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button onClick={() => editar(c)} style={miniBtn}>Editar</button>
                    <button onClick={() => eliminar(c)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {form && (
        <div style={{ marginTop: 14, padding: 14, background: '#fafbfc', border: '1px solid #eee', borderRadius: 10 }}>
          <div style={grid(4)}>
            <Campo label="Nombre" requerido valor={form.nombre} onChange={v => setForm(p => ({ ...p, nombre: v }))} />
            <Campo label="Parentesco" opciones={listas.rrhh_parentesco || []} valor={form.parentesco_id} onChange={v => setForm(p => ({ ...p, parentesco_id: v }))} />
            <Campo label="Telefono" requerido valor={form.telefono} onChange={v => setForm(p => ({ ...p, telefono: v }))} />
            <Campo label="Telefono secundario" valor={form.telefono_secundario} onChange={v => setForm(p => ({ ...p, telefono_secundario: v }))} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setForm(null)} style={btnSecundario}>Cancelar</button>
            <button onClick={submit} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar contacto'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ficha laboral (absorbe lo contractual, spec §5) ─────────────────────
function TabFichaLaboral({ exp, listas, guardar, onAccion }) {
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);

  const abrir = () => {
    setForm({
      fecha_ingreso: paraInputFecha(exp.fecha_ingreso),
      horario: exp.horario || '', modalidad_id: exp.modalidad_id,
      direccion_teletrabajo: exp.direccion_teletrabajo || '',
      estado_laboral: exp.estado_laboral || '',
      contrato_inicia: paraInputFecha(exp.contrato_inicia),
      contrato_vence: paraInputFecha(exp.contrato_vence),
      periodo_prueba_hasta: paraInputFecha(exp.periodo_prueba_hasta)
    });
    setEditando(true);
  };
  const submit = async () => {
    setGuardando(true);
    const ok = await guardar(form);
    setGuardando(false);
    if (ok) setEditando(false);
  };

  // La modalidad elegida en el formulario decide si la direccion aplica (§5.1).
  const modalidadSel = (listas.rrhh_modalidad_trabajo || []).find(m => String(m.id) === String(form.modalidad_id));
  const esRemota = modalidadSel && MODALIDADES_REMOTAS.includes(modalidadSel.valor);
  const contratoVence = CONTRATOS_CON_VENCIMIENTO.includes(exp.tipo_contrato);

  return (
    <Panel titulo="Ficha laboral" editando={editando} guardando={guardando}
      onEditar={abrir} onCancelar={() => setEditando(false)} onGuardar={submit}>
      <div style={grid(3)}>
        <DatoBloqueado label="Puesto" valor={exp.puesto} />
        <DatoBloqueado label="Departamento" valor={exp.departamento} />
        <DatoBloqueado label="Jefatura" valor={exp.jefatura_nombre} />
        <DatoBloqueado label="Jornada laboral" valor={exp.jornada} />
        {editando ? (
          <>
            <Campo label="Fecha de ingreso" tipo="date" valor={form.fecha_ingreso} onChange={v => setForm(p => ({ ...p, fecha_ingreso: v }))} />
            <Campo label="Estado laboral" opciones={ESTADOS_LABORALES.map(e => ({ id: e, valor: e }))} valor={form.estado_laboral} onChange={v => setForm(p => ({ ...p, estado_laboral: v }))} />
            <Campo label="Modalidad" opciones={listas.rrhh_modalidad_trabajo || []} valor={form.modalidad_id} onChange={v => setForm(p => ({ ...p, modalidad_id: v }))} />
            <Campo label="Horario" ancho={esRemota ? 1 : 2} valor={form.horario} onChange={v => setForm(p => ({ ...p, horario: v }))} />
            {esRemota && (
              <Campo label="Direccion de teletrabajo" requerido valor={form.direccion_teletrabajo}
                onChange={v => setForm(p => ({ ...p, direccion_teletrabajo: v }))} />
            )}
          </>
        ) : (
          <>
            <Dato label="Fecha de ingreso" valor={formatFecha(exp.fecha_ingreso)} />
            <Dato label="Estado laboral" valor={exp.estado_laboral} />
            <Dato label="Modalidad" valor={exp.modalidad} />
            <Dato label="Horario" valor={exp.horario} />
            {MODALIDADES_REMOTAS.includes(exp.modalidad) && (
              <Dato label="Direccion de teletrabajo" ancho={2} valor={exp.direccion_teletrabajo} />
            )}
          </>
        )}
      </div>

      {/* Bloque contractual: ya no es una pestana aparte */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#012E40', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '12px' }}>
          Contrato
        </div>
        <div style={grid(3)}>
          <DatoBloqueado label="Tipo de contrato" valor={exp.tipo_contrato} />
          {editando ? (
            <>
              <Campo label="Contrato inicia" tipo="date" valor={form.contrato_inicia} onChange={v => setForm(p => ({ ...p, contrato_inicia: v }))} />
              {contratoVence && (
                <Campo label="Contrato vence" requerido tipo="date" valor={form.contrato_vence} onChange={v => setForm(p => ({ ...p, contrato_vence: v }))} />
              )}
              <Campo label="Periodo de prueba hasta" tipo="date" valor={form.periodo_prueba_hasta} onChange={v => setForm(p => ({ ...p, periodo_prueba_hasta: v }))} />
            </>
          ) : (
            <>
              <Dato label="Contrato inicia" valor={formatFecha(exp.contrato_inicia)} />
              {contratoVence && <Dato label="Contrato vence" valor={formatFecha(exp.contrato_vence)} />}
              <Dato label="Periodo de prueba hasta" valor={formatFecha(exp.periodo_prueba_hasta)} />
            </>
          )}
        </div>
        {!contratoVence && exp.tipo_contrato && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
            Un contrato {exp.tipo_contrato} no lleva fecha de vencimiento.
          </div>
        )}
      </div>

      <NotaAccion
        texto="Puesto, departamento, jefatura, jornada y tipo de contrato solo cambian por accion de personal. Las fechas se editan aqui y quedan en la bitacora."
        onAccion={onAccion} tipos={['Puesto', 'Traslado', 'Jefatura', 'Jornada', 'Contrato']} />
    </Panel>
  );
}

// ── Salarial ────────────────────────────────────────────────────────────
function TabSalarial({ exp, listas, guardar, onAccion }) {
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);

  const abrir = () => { setForm({ banco_id: exp.banco_id, cuenta_iban: exp.cuenta_iban || '' }); setEditando(true); };
  const submit = async () => {
    setGuardando(true);
    const ok = await guardar(form);
    setGuardando(false);
    if (ok) setEditando(false);
  };

  return (
    <Panel titulo="Salario y cuenta bancaria" editando={editando} guardando={guardando}
      onEditar={abrir} onCancelar={() => setEditando(false)} onGuardar={submit}>
      <div style={{ fontSize: 11, color: '#8A6100', background: '#FDF4E0', border: '1px solid #f0e0b8', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
        La consulta de este expediente queda registrada en la bitacora por contener datos sensibles.
      </div>
      <div style={grid(3)}>
        <DatoBloqueado label="Salario" valor={fmtMonto(exp.salario, exp.moneda)} />
        <DatoBloqueado label="Moneda" valor={exp.moneda} />
        <DatoBloqueado label="Modalidad de pago" valor={exp.periodicidad_pago} />
        {editando ? (
          <>
            <Campo label="Banco" opciones={listas.rrhh_banco || []} valor={form.banco_id} onChange={v => setForm(p => ({ ...p, banco_id: v }))} />
            <Campo label="Cuenta IBAN" ancho={2} valor={form.cuenta_iban} onChange={v => setForm(p => ({ ...p, cuenta_iban: v }))} />
          </>
        ) : (
          <>
            <Dato label="Banco" valor={exp.banco} />
            <Dato label="Cuenta IBAN" ancho={2} valor={exp.cuenta_iban} />
          </>
        )}
      </div>
      <NotaAccion texto="El salario solo cambia por accion de personal." onAccion={onAccion} tipos={['Salario']} />
    </Panel>
  );
}

// Nota + accesos directos para registrar la accion del tipo que corresponde.
function NotaAccion({ texto, onAccion, tipos }) {
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: '#888' }}>&#128274; {texto}</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tipos.map(t => (
          <button key={t} onClick={() => onAccion(t)} style={miniBtn}>Accion: {t}</button>
        ))}
      </div>
    </div>
  );
}

// ── Documentos del expediente ───────────────────────────────────────────
function TabDocumentos({ colaboradorId, setError, setAviso }) {
  const [docs, setDocs]     = useState([]);
  const [tipos, setTipos]   = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [form, setForm]     = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [rd, rt] = await Promise.all([
        axios.get(`${API}/rrhh-documentos?colaborador_id=${colaboradorId}`),
        axios.get(`${API}/rrhh-catalogos?recurso=tipos-documento`)
      ]);
      setDocs(rd.data || []);
      setTipos(rt.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [colaboradorId, setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirSubida = () => setForm({ modo: 'nuevo', tipo_documento_id: null, archivo: null, fecha_documento: '', fecha_vencimiento: '' });
  // Reemplazo (H-5): una sola operacion sube la version nueva e inactiva la vieja.
  const abrirReemplazo = (d) => setForm({ modo: 'reemplazo', reemplaza: d, tipo_documento_id: d.tipo_documento_id, archivo: null, fecha_documento: '', fecha_vencimiento: '' });

  const subir = async () => {
    if (!form.tipo_documento_id || !form.archivo) { setError('Elegi el tipo documental y el archivo.'); return; }
    if (form.archivo.size > 8 * 1024 * 1024) { setError('El archivo supera los 8 MB. Comprimilo o subilo en partes.'); return; }
    setSubiendo(true); setError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1]);
        fr.onerror = reject;
        fr.readAsDataURL(form.archivo);
      });
      const cuerpo = {
        colaborador_id: parseInt(colaboradorId),
        tipo_documento_id: form.tipo_documento_id,
        nombre_archivo: form.archivo.name,
        content_type: form.archivo.type || 'application/octet-stream',
        fecha_documento: form.fecha_documento || null,
        fecha_vencimiento: form.fecha_vencimiento || null,
        contenido_base64: base64
      };
      const url = form.modo === 'reemplazo'
        ? `${API}/rrhh-documentos?action=reemplazar&id=${form.reemplaza.id}`
        : `${API}/rrhh-documentos`;
      await axios.post(url, cuerpo);
      setForm(null);
      setAviso(form.modo === 'reemplazo' ? 'Documento reemplazado. La version anterior quedo inactiva.' : 'Documento subido.');
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setSubiendo(false); }
  };

  const descargar = async (d) => {
    setError('');
    try {
      const r = await axios.get(`${API}/rrhh-documentos?action=descargar&id=${d.id}`);
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch (e) { setError(msgError(e)); }
  };

  const eliminar = async (d) => {
    if (!window.confirm(`Quitar "${d.nombre_archivo}" del expediente? El archivo no se destruye, queda inactivo.`)) return;
    try { await axios.delete(`${API}/rrhh-documentos?id=${d.id}`); await cargar(); }
    catch (e) { setError(msgError(e)); }
  };

  const hoy = new Date().toISOString().slice(0, 10);
  const tipoSel = tipos.find(t => String(t.id) === String(form?.tipo_documento_id));

  return (
    <div style={{ ...tarjeta, padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>Documentos del expediente</h3>
        <button onClick={abrirSubida} style={btnPrimario}>+ Subir documento</button>
      </div>

      {form && (
        <div style={{ marginBottom: 16, padding: 14, background: '#fafbfc', border: '1px solid #eee', borderRadius: 10 }}>
          {form.modo === 'reemplazo' && (
            <div style={{ fontSize: 12, color: '#185FA5', marginBottom: 10 }}>
              Reemplazando <b>{form.reemplaza.nombre_archivo}</b> ({form.reemplaza.tipo}). La version anterior queda inactiva, no se borra.
            </div>
          )}
          <div style={grid(4)}>
            <Campo label="Tipo documental" requerido valor={form.tipo_documento_id}
              opciones={tipos.map(t => ({ id: t.id, valor: `${t.bloque} · ${t.nombre}` }))}
              deshabilitado={form.modo === 'reemplazo'}
              onChange={v => setForm(p => ({ ...p, tipo_documento_id: v }))} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Archivo *</label>
              <input type="file" onChange={e => setForm(p => ({ ...p, archivo: e.target.files[0] }))} style={{ fontSize: 12 }} />
            </div>
            <Campo label="Fecha del documento" tipo="date" valor={form.fecha_documento} onChange={v => setForm(p => ({ ...p, fecha_documento: v }))} />
            <Campo label="Vence" requerido={!!tipoSel?.vence} tipo="date" valor={form.fecha_vencimiento} onChange={v => setForm(p => ({ ...p, fecha_vencimiento: v }))} />
          </div>
          {tipoSel?.vence && !form.fecha_vencimiento && (
            <div style={{ fontSize: 11, color: '#8A6100', marginTop: 8 }}>
              Este tipo documental vence: la fecha de vencimiento es obligatoria.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setForm(null)} style={btnSecundario}>Cancelar</button>
            <button onClick={subir} disabled={subiendo} style={{ ...btnNavy, opacity: subiendo ? 0.7 : 1 }}>
              {subiendo ? 'Subiendo...' : (form.modo === 'reemplazo' ? 'Reemplazar' : 'Subir')}
            </button>
          </div>
        </div>
      )}

      {cargando ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>
        : docs.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#bbb' }}>Sin documentos.</div>
        : ['Personal', 'Administrativo'].map(bloque => {
          const delBloque = docs.filter(d => d.bloque === bloque);
          if (!delBloque.length) return null;
          return (
            <div key={bloque} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Expediente {bloque}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: '#f8f9fa' }}>
                  {['Tipo', 'Archivo', 'Fecha doc.', 'Vence', 'Tamano', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {delBloque.map(d => {
                    const vencido = d.fecha_vencimiento && d.fecha_vencimiento.slice(0, 10) < hoy;
                    return (
                      <tr key={d.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                        <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{d.tipo}</td>
                        <td style={tdStyle}>{d.nombre_archivo}</td>
                        <td style={tdStyle}>{formatFecha(d.fecha_documento)}</td>
                        <td style={tdStyle}>
                          {d.fecha_vencimiento
                            ? <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {formatFecha(d.fecha_vencimiento)}
                                {vencido && <Badge texto="Vencido" tono="rojo" />}
                              </span>
                            : '—'}
                        </td>
                        <td style={tdStyle}>{fmtBytes(d.tamano_bytes)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => descargar(d)} style={miniBtn}>Descargar</button>
                          <button onClick={() => abrirReemplazo(d)} style={{ ...miniBtn, marginLeft: 6 }}>Reemplazar</button>
                          <button onClick={() => eliminar(d)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Quitar</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
    </div>
  );
}

// ── Formacion academica — campos condicionales por tipo (§7.1) ──────────
function TabFormacion({ colaboradorId, listas, setError, recargar }) {
  const [filas, setFilas] = useState([]);
  const [form, setForm]   = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/rrhh-colaboradores?action=formacion&id=${colaboradorId}`);
      setFilas(r.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [colaboradorId, setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const vacio = (tipo) => ({
    tipo_registro: tipo, titulo: '', institucion: '', grado_academico_id: null,
    idioma_id: null, nivel_idioma_id: null, empresa: '', puesto_previo: '',
    fecha_desde: '', fecha_hasta: ''
  });
  // Cada bloque crea registros de SU tipo: el bloque decide, no un selector.
  // Asi nadie abre "agregar" desde Experiencia, cambia a Idioma sin querer, y ve
  // desaparecer el registro hacia otro bloque.
  const nuevoEn = (tipo) => setForm(vacio(tipo));
  const editar = (x) => setForm({
    id: x.id, tipo_registro: x.tipo_registro, titulo: x.titulo || '', institucion: x.institucion || '',
    grado_academico_id: x.grado_academico_id, idioma_id: x.idioma_id, nivel_idioma_id: x.nivel_idioma_id,
    empresa: x.empresa || '', puesto_previo: x.puesto_previo || '',
    fecha_desde: paraInputFecha(x.fecha_desde), fecha_hasta: paraInputFecha(x.fecha_hasta)
  });

  const submit = async () => {
    setGuardando(true); setError('');
    const body = { ...form };
    ['fecha_desde', 'fecha_hasta'].forEach(k => { if (!body[k]) body[k] = null; });
    try {
      if (form.id) await axios.put(`${API}/rrhh-colaboradores?action=formacion&id=${form.id}`, body);
      else         await axios.post(`${API}/rrhh-colaboradores?action=formacion&id=${colaboradorId}`, body);
      setForm(null);
      await cargar();
      await recargar();   // el ultimo grado academico se deriva de aca
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const eliminar = async (x) => {
    if (!window.confirm('Eliminar este registro de formacion?')) return;
    try {
      await axios.delete(`${API}/rrhh-colaboradores?action=formacion&id=${x.id}`);
      await cargar(); await recargar();
    } catch (e) { setError(msgError(e)); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // El orden de Experiencia, Titulo y Curso viene resuelto del backend (vigentes
  // primero, luego por fecha de inicio descendente). Los idiomas no tienen fecha,
  // asi que se ordenan aca por nombre.
  const porTipo = (tipo) => filas.filter(x => x.tipo_registro === tipo);
  const idiomas = porTipo('Idioma')
    .slice()
    .sort((a, b) => (a.idioma || '').localeCompare(b.idioma || '', 'es'));

  const acciones = (x) => (
    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
      <button onClick={() => editar(x)} style={miniBtn}>Editar</button>
      <button onClick={() => eliminar(x)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Eliminar</button>
    </td>
  );

  // El formulario se dibuja DENTRO del bloque al que pertenece el registro, para
  // que se vea de donde salio y adonde va a caer al guardarse.
  const formularioDe = (tipo) => (form && form.tipo_registro === tipo) ? (
    <div style={{ marginBottom: 16, padding: 14, background: '#fafbfc', border: '1px solid #eee', borderRadius: 10 }}>
      {tipo === 'Titulo' && (
        <div style={grid(4)}>
          <Campo label="Grado academico" requerido opciones={listas.rrhh_grado_academico || []} valor={form.grado_academico_id} onChange={v => f('grado_academico_id', v)} />
          <Campo label="Titulo" requerido valor={form.titulo} onChange={v => f('titulo', v)} />
          <Campo label="Institucion" valor={form.institucion} onChange={v => f('institucion', v)} />
          <Campo label="Fecha de obtencion" tipo="date" valor={form.fecha_hasta} onChange={v => f('fecha_hasta', v)} />
        </div>
      )}
      {tipo === 'Curso' && (
        <div style={grid(3)}>
          <Campo label="Nombre" requerido valor={form.titulo} onChange={v => f('titulo', v)} />
          <Campo label="Institucion" valor={form.institucion} onChange={v => f('institucion', v)} />
          <Campo label="Fecha" tipo="date" valor={form.fecha_hasta} onChange={v => f('fecha_hasta', v)} />
        </div>
      )}
      {tipo === 'Idioma' && (
        <div style={grid(2)}>
          <Campo label="Idioma" requerido opciones={listas.rrhh_idioma || []} valor={form.idioma_id} onChange={v => f('idioma_id', v)} />
          <Campo label="Nivel" requerido opciones={listas.rrhh_nivel_idioma || []} valor={form.nivel_idioma_id} onChange={v => f('nivel_idioma_id', v)} />
        </div>
      )}
      {tipo === 'Experiencia' && (
        <>
          <div style={grid(4)}>
            <Campo label="Empresa" requerido valor={form.empresa} onChange={v => f('empresa', v)} />
            <Campo label="Puesto desempenado" requerido valor={form.puesto_previo} onChange={v => f('puesto_previo', v)} />
            <Campo label="Desde" requerido tipo="date" valor={form.fecha_desde} onChange={v => f('fecha_desde', v)} />
            <Campo label="Hasta" tipo="date" valor={form.fecha_hasta} onChange={v => f('fecha_hasta', v)} />
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
            Dejá "Hasta" en blanco si es el trabajo actual: asi se marca como vigente y encabeza la lista.
          </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button onClick={() => setForm(null)} style={btnSecundario}>Cancelar</button>
        <button onClick={submit} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  ) : null;

  if (cargando) return <div style={{ ...tarjeta, padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>;

  return (
    <>
      {/* ── Experiencia laboral ─────────────────────────────────────── */}
      <BloqueFormacion titulo="Experiencia laboral" onAgregar={() => nuevoEn('Experiencia')}
        vacio={porTipo('Experiencia').length === 0} leyendaVacio="Sin experiencia laboral registrada."
        formulario={formularioDe('Experiencia')}>
        <table style={tablaStyle}>
          <thead><tr style={{ background: '#f8f9fa' }}>
            {['Empresa', 'Puesto desempeñado', 'Desde', 'Hasta', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>
            {porTipo('Experiencia').map(x => (
              <tr key={x.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{x.empresa || '—'}</td>
                <td style={tdStyle}>{x.puesto_previo || '—'}</td>
                <td style={tdStyle}>{formatFecha(x.fecha_desde)}</td>
                <td style={tdStyle}>
                  {x.fecha_hasta ? formatFecha(x.fecha_hasta) : <Badge texto="Vigente" tono="verde" />}
                </td>
                {acciones(x)}
              </tr>
            ))}
          </tbody>
        </table>
      </BloqueFormacion>

      {/* ── Formacion academica (solo titulos) ──────────────────────── */}
      <BloqueFormacion titulo="Formación académica" onAgregar={() => nuevoEn('Titulo')}
        vacio={porTipo('Titulo').length === 0} leyendaVacio="Sin títulos registrados."
        formulario={formularioDe('Titulo')}>
        <table style={tablaStyle}>
          <thead><tr style={{ background: '#f8f9fa' }}>
            {['Grado académico', 'Título', 'Institución', 'Fecha', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>
            {porTipo('Titulo').map(x => (
              <tr key={x.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                <td style={tdStyle}><Badge texto={x.grado_academico || '—'} tono="azul" /></td>
                <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{x.titulo || '—'}</td>
                <td style={tdStyle}>{x.institucion || '—'}</td>
                <td style={tdStyle}>{formatFecha(x.fecha_hasta)}</td>
                {acciones(x)}
              </tr>
            ))}
          </tbody>
        </table>
      </BloqueFormacion>

      {/* ── Cursos y certificaciones ────────────────────────────────── */}
      <BloqueFormacion titulo="Cursos y certificaciones" onAgregar={() => nuevoEn('Curso')}
        vacio={porTipo('Curso').length === 0} leyendaVacio="Sin cursos ni certificaciones registrados."
        formulario={formularioDe('Curso')}>
        <table style={tablaStyle}>
          <thead><tr style={{ background: '#f8f9fa' }}>
            {['Nombre', 'Institución', 'Fecha', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>
            {porTipo('Curso').map(x => (
              <tr key={x.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{x.titulo || '—'}</td>
                <td style={tdStyle}>{x.institucion || '—'}</td>
                <td style={tdStyle}>{formatFecha(x.fecha_hasta)}</td>
                {acciones(x)}
              </tr>
            ))}
          </tbody>
        </table>
      </BloqueFormacion>

      {/* ── Idiomas ─────────────────────────────────────────────────── */}
      <BloqueFormacion titulo="Idiomas" onAgregar={() => nuevoEn('Idioma')}
        vacio={idiomas.length === 0} leyendaVacio="Sin idiomas registrados."
        formulario={formularioDe('Idioma')}>
        <table style={tablaStyle}>
          <thead><tr style={{ background: '#f8f9fa' }}>
            {['Idioma', 'Nivel', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>
            {idiomas.map(x => (
              <tr key={x.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{x.idioma || '—'}</td>
                <td style={tdStyle}>{x.nivel_idioma || '—'}</td>
                {acciones(x)}
              </tr>
            ))}
          </tbody>
        </table>
      </BloqueFormacion>
    </>
  );
}

const tablaStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };

// Un bloque de la pestaña: encabezado, su boton de agregar, su formulario y su
// tabla. Un bloque sin registros NO se esconde — se muestra con su leyenda, para
// que se vea que la sección existe y se puede llenar (v50 §5.2.4).
function BloqueFormacion({ titulo, onAgregar, vacio, leyendaVacio, formulario, children }) {
  return (
    <div style={{ ...tarjeta, padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>{titulo}</h3>
        <button onClick={onAgregar} style={btnSecundario}>+ Agregar</button>
      </div>
      {formulario}
      {vacio
        ? <div style={{ padding: '20px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>{leyendaVacio}</div>
        : children}
    </div>
  );
}

// ── Activos asignados (D11) ─────────────────────────────────────────────
function TabActivos({ colaboradorId, listas, setError }) {
  const [filas, setFilas] = useState([]);
  const [form, setForm]   = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/rrhh-colaboradores?action=activos&id=${colaboradorId}`);
      setFilas(r.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [colaboradorId, setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const nuevo = () => setForm({ equipo: '', numero_activo: '', fecha_entrega: '', estado_id: null, responsable: '', fecha_devolucion: '', notas: '' });
  const editar = (x) => setForm({
    id: x.id, equipo: x.equipo || '', numero_activo: x.numero_activo || '',
    fecha_entrega: paraInputFecha(x.fecha_entrega), estado_id: x.estado_id, responsable: x.responsable || '',
    fecha_devolucion: paraInputFecha(x.fecha_devolucion), notas: x.notas || ''
  });

  const submit = async () => {
    if (!form.equipo?.trim()) { setError('El equipo es obligatorio.'); return; }
    setGuardando(true); setError('');
    const body = { ...form };
    ['fecha_entrega', 'fecha_devolucion'].forEach(k => { if (!body[k]) body[k] = null; });
    try {
      if (form.id) await axios.put(`${API}/rrhh-colaboradores?action=activos&id=${form.id}`, body);
      else         await axios.post(`${API}/rrhh-colaboradores?action=activos&id=${colaboradorId}`, body);
      setForm(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const eliminar = async (x) => {
    if (!window.confirm(`Eliminar el activo "${x.equipo}"?`)) return;
    try { await axios.delete(`${API}/rrhh-colaboradores?action=activos&id=${x.id}`); await cargar(); }
    catch (e) { setError(msgError(e)); }
  };

  return (
    <div style={{ ...tarjeta, padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>Activos asignados</h3>
        <button onClick={nuevo} style={btnSecundario}>+ Asignar activo</button>
      </div>

      {form && (
        <div style={{ marginBottom: 16, padding: 14, background: '#fafbfc', border: '1px solid #eee', borderRadius: 10 }}>
          <div style={grid(3)}>
            <Campo label="Equipo" requerido ancho={2} valor={form.equipo} onChange={v => setForm(p => ({ ...p, equipo: v }))} />
            <Campo label="Numero de activo" valor={form.numero_activo} onChange={v => setForm(p => ({ ...p, numero_activo: v }))} />
            <Campo label="Fecha de entrega" tipo="date" valor={form.fecha_entrega} onChange={v => setForm(p => ({ ...p, fecha_entrega: v }))} />
            <Campo label="Estado" opciones={listas.rrhh_estado_activo || []} valor={form.estado_id} onChange={v => setForm(p => ({ ...p, estado_id: v }))} />
            <Campo label="Responsable" valor={form.responsable} onChange={v => setForm(p => ({ ...p, responsable: v }))} />
            <Campo label="Fecha de devolucion" tipo="date" valor={form.fecha_devolucion} onChange={v => setForm(p => ({ ...p, fecha_devolucion: v }))} />
            <Campo label="Notas" ancho={2} valor={form.notas} onChange={v => setForm(p => ({ ...p, notas: v }))} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setForm(null)} style={btnSecundario}>Cancelar</button>
            <button onClick={submit} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {cargando ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>
        : filas.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#bbb' }}>Sin activos asignados.</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#f8f9fa' }}>
              {['Equipo', 'N° activo', 'Entrega', 'Estado', 'Responsable', 'Devolucion', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filas.map(x => (
                <tr key={x.id} style={{ borderBottom: '1px solid #f3f3f3', opacity: x.fecha_devolucion ? 0.6 : 1 }}>
                  <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{x.equipo}</td>
                  <td style={tdStyle}>{x.numero_activo || '—'}</td>
                  <td style={tdStyle}>{formatFecha(x.fecha_entrega)}</td>
                  <td style={tdStyle}>{x.estado || '—'}</td>
                  <td style={tdStyle}>{x.responsable || '—'}</td>
                  <td style={tdStyle}>{x.fecha_devolucion ? formatFecha(x.fecha_devolucion) : <Badge texto="En uso" tono="verde" />}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button onClick={() => editar(x)} style={miniBtn}>Editar</button>
                    <button onClick={() => eliminar(x)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── Historial: acciones de personal + periodos laborales cerrados ───────
function TabHistorial({ colaboradorId, listas, colaboradores, moneda }) {
  const [filas, setFilas] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierta, setAbierta] = useState(null);
  const [error, setErrorLocal] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [ra, rp] = await Promise.all([
          axios.get(`${API}/rrhh-acciones-personal?colaborador_id=${colaboradorId}`),
          axios.get(`${API}/rrhh-colaboradores?action=periodos&id=${colaboradorId}`)
        ]);
        setFilas(ra.data || []);
        setPeriodos(rp.data || []);
      } catch (e) { setErrorLocal(msgError(e)); }
      setCargando(false);
    })();
  }, [colaboradorId]);

  const abrir = async (fila) => {
    if (abierta?.id === fila.id) { setAbierta(null); return; }
    try {
      const r = await axios.get(`${API}/rrhh-acciones-personal?id=${fila.id}`);
      setAbierta(r.data);
    } catch (e) { setErrorLocal(msgError(e)); }
  };

  return (
    <>
      {periodos.length > 0 && (
        <div style={{ ...tarjeta, padding: '20px', marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: '700', color: '#012E40' }}>
            Periodos laborales anteriores
          </h3>
          <p style={{ fontSize: 12, color: '#888', marginTop: 0, marginBottom: 12 }}>
            El periodo vigente vive en la ficha laboral; aca solo estan los cerrados.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#f8f9fa' }}>
              {['Ingreso', 'Salida', 'Motivo', 'Notas', 'Registrado por'].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>
              {periodos.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                  <td style={{ ...tdStyle, color: '#012E40' }}>{formatFecha(p.fecha_ingreso)}</td>
                  <td style={tdStyle}>{formatFecha(p.fecha_salida)}</td>
                  <td style={tdStyle}>{p.motivo_salida || '—'}</td>
                  <td style={tdStyle}>{p.notas || '—'}</td>
                  <td style={tdStyle}>{p.cerrado_por || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ ...tarjeta, padding: '20px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '700', color: '#012E40' }}>
          Historial de movimientos
        </h3>
        <Aviso texto={error} />

        {cargando ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>
          : filas.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#bbb' }}>Sin acciones de personal registradas.</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#f8f9fa' }}>
                {['Registrada', 'Tipo', 'Rige desde', 'Estado', 'Aplicada', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filas.map(a => (
                  <React.Fragment key={a.id}>
                    <tr onClick={() => abrir(a)} style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer' }}>
                      <td style={tdStyle}>{formatFecha(a.fecha_registro)}</td>
                      <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{a.tipo}</td>
                      <td style={tdStyle}>{formatFecha(a.rige_desde)}</td>
                      <td style={tdStyle}><Badge texto={a.estado} tono={tonoEstadoAccion(a.estado)} /></td>
                      <td style={tdStyle}>{formatFecha(a.fecha_aplicacion)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#aaa' }}>{abierta?.id === a.id ? '▲' : '▼'}</td>
                    </tr>
                    {abierta?.id === a.id && (
                      <tr><td colSpan={6} style={{ background: '#fafbfc', padding: '14px' }}>
                        <div style={grid(3)}>
                          <Dato label="Valor anterior" valor={textoValorAccion(abierta.tipo, abierta.valor_anterior, listas, colaboradores, moneda)} />
                          <Dato label="Valor nuevo" valor={textoValorAccion(abierta.tipo, abierta.valor_nuevo, listas, colaboradores, moneda)} />
                          <Dato label="Motivo de la solicitud" valor={abierta.motivo} />
                          <Dato label="Registrada por" valor={abierta.registrada_por} />
                          <Dato label="Aprobacion RRHH" valor={abierta.aprob_rrhh_omitida
                            ? `Omitida — ${abierta.motivo_omision}`
                            : (abierta.aprob_rrhh_por ? `${abierta.aprob_rrhh_por} · ${formatFecha(abierta.aprob_rrhh_fecha)}` : null)} />
                          <Dato label="Aprobacion Gerencia" valor={abierta.aprob_gerencia_omitida
                            ? `Omitida — ${abierta.motivo_omision}`
                            : (abierta.aprob_gerencia_por ? `${abierta.aprob_gerencia_por} · ${formatFecha(abierta.aprob_gerencia_fecha)}` : null)} />
                          {abierta.motivo_rechazo && <Dato label="Motivo del rechazo" ancho={2} valor={abierta.motivo_rechazo} />}
                          <Dato label="Correo al colaborador" valor={abierta.correo_enviado_fecha ? formatFecha(abierta.correo_enviado_fecha) : 'No enviado'} />
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </>
  );
}

// ── Modal de baja: cierra el periodo laboral vigente (H-1) ──────────────
function ModalBaja({ colaboradorId, listas, onClose, onHecho }) {
  const [fechaSalida, setFecha] = useState('');
  const [motivoId, setMotivo]   = useState(null);
  const [notas, setNotas]       = useState('');
  const [error, setError]       = useState('');
  const [guardando, setGuardando] = useState(false);

  const confirmar = async () => {
    if (!fechaSalida) { setError('La fecha de salida es obligatoria.'); return; }
    setGuardando(true); setError('');
    try {
      await axios.post(`${API}/rrhh-colaboradores?action=baja&id=${colaboradorId}`, {
        fecha_salida: fechaSalida, motivo_salida_id: motivoId, notas: notas || null
      });
      onHecho();
    } catch (e) { setError(msgError(e)); setGuardando(false); }
  };

  return (
    <ModalChico titulo="Dar de baja el expediente" onClose={onClose}>
      <Aviso texto={error} />
      <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>
        Se cierra el periodo laboral vigente y el expediente queda Inactivo / Terminado.
        No se borra ningun dato ni documento: si la persona vuelve, se reactiva el mismo expediente.
      </p>
      <div style={grid(2)}>
        <Campo label="Fecha de salida" requerido tipo="date" valor={fechaSalida} onChange={setFecha} />
        <Campo label="Motivo de salida" opciones={listas.rrhh_motivo_salida || []} valor={motivoId} onChange={setMotivo} />
        <div style={{ gridColumn: '1/-1' }}>
          <Campo label="Notas" valor={notas} onChange={setNotas} />
        </div>
      </div>
      <PieModal onClose={onClose} onConfirmar={confirmar} guardando={guardando} texto="Dar de baja" />
    </ModalChico>
  );
}

// ── Modal de reactivacion: arranca un periodo nuevo (A11) ───────────────
function ModalReactivar({ colaboradorId, onClose, onHecho }) {
  const [fechaIngreso, setFecha] = useState('');
  const [estado, setEstado]      = useState('Preingreso');
  const [error, setError]        = useState('');
  const [guardando, setGuardando] = useState(false);

  const confirmar = async () => {
    if (!fechaIngreso) { setError('La nueva fecha de ingreso es obligatoria.'); return; }
    setGuardando(true); setError('');
    try {
      await axios.post(`${API}/rrhh-colaboradores?action=reactivar&id=${colaboradorId}`, {
        fecha_ingreso: fechaIngreso, estado_laboral: estado
      });
      onHecho();
    } catch (e) { setError(msgError(e)); setGuardando(false); }
  };

  return (
    <ModalChico titulo="Reactivar expediente (recontratacion)" onClose={onClose}>
      <Aviso texto={error} />
      <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>
        Se conserva el mismo codigo, los titulos, los documentos, los activos y el historial completo.
        Solo arranca un periodo laboral nuevo; el anterior queda en el historial.
      </p>
      <div style={grid(2)}>
        <Campo label="Nueva fecha de ingreso" requerido tipo="date" valor={fechaIngreso} onChange={setFecha} />
        <Campo label="Estado laboral" opciones={[{ id: 'Preingreso', valor: 'Preingreso' }, { id: 'Periodo de prueba', valor: 'Periodo de prueba' }]}
          valor={estado} onChange={setEstado} />
      </div>
      <PieModal onClose={onClose} onConfirmar={confirmar} guardando={guardando} texto="Reactivar" />
    </ModalChico>
  );
}

function ModalChico({ titulo, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden' }}>
        <div style={{ background: '#012E40', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{titulo}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>&#10005;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function PieModal({ onClose, onConfirmar, guardando, texto }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
      <button onClick={onClose} style={btnSecundario}>Cancelar</button>
      <button onClick={onConfirmar} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
        {guardando ? 'Guardando...' : texto}
      </button>
    </div>
  );
}

const miniBtn = {
  padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px',
  fontSize: '12px', cursor: 'pointer', color: '#555'
};
